// client/src/components/LlamaIndexDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';

const LlamaIndexDashboard = () => {
    const [httpInput, setHttpInput] = useState('');
    const [streamInput, setStreamInput] = useState('');
    const [httpResult, setHttpResult] = useState('');
    const [streamResult, setStreamResult] = useState('');
    const [loading, setLoading] = useState(false);
    const [socket, setSocket] = useState(null);
    const [indexStatus, setIndexStatus] = useState('Not loaded');

    useEffect(() => {
        checkIndexStatus();
        const BASE_URL = process.env.REACT_APP_LLAMAINDEX_BASE_URL;
        // const currentSocket = io('http://localhost:4080', {
        const currentSocket = io(BASE_URL, {
            withCredentials: true
        });
        
        currentSocket.on('connect', () => {
            console.log('Connected to LlamaIndexService');
        });

        currentSocket.on('stream_response', (message) => {
            console.log('Received chunk:', message);
            const chunk = typeof message === 'object' ? message.chunk : message;
            setStreamResult(prevResult => prevResult + (chunk || ''));
        });

        currentSocket.on('stream_complete', () => {
            console.log('Stream complete');
            if (currentSocket.timeoutId) {
                clearTimeout(currentSocket.timeoutId);
            }
            setLoading(false);
        });

        currentSocket.on('stream_error', ({ error }) => {
            console.error('Stream error:', error);
            if (currentSocket.timeoutId) {
                clearTimeout(currentSocket.timeoutId);
            }
            setStreamResult(prev => prev + '\nError: ' + error);
            setLoading(false);
        });

        currentSocket.on('disconnect', () => {
            console.log('Disconnected from LlamaIndexService');
            if (currentSocket.timeoutId) {
                clearTimeout(currentSocket.timeoutId);
            }
            setLoading(false);
        });

        setSocket(currentSocket);

        return () => {
            if (currentSocket.timeoutId) {
                clearTimeout(currentSocket.timeoutId);
            }
            currentSocket.disconnect();
        };
    }, []);

    const checkIndexStatus = async () => {
        try {
            // const response = await axios.get('http://localhost:4080/index-status');
            const response = await axios.get(process.env.REACT_APP_LLAMAINDEX_EXTENDED_NEURO_URL + '/index-status');
            const statusValue = response.data?.status || response.data;
            console.log('Current index status:', statusValue);
            setIndexStatus(statusValue);
        } catch (error) {
            console.error('Error checking index status:', error);
            setIndexStatus('Error');
        }
    };

    const handleLoadAndIndex = async () => {
        setLoading(true);
        try {
            // const response = await axios.post('http://localhost:4080/load-and-index', null, {
            const response = await axios.post(process.env.REACT_APP_LLAMAINDEX_EXTENDED_NEURO_URL + '/load-and-index', null, {
                responseType: 'text'
            });
            setHttpResult(response.data);
            setIndexStatus('Loaded');
        } catch (error) {
            console.error('Error loading and indexing:', error);
            setHttpResult('Error: ' + (error.response?.data || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleHttpQuery = async () => {
        if (!httpInput.trim()) return;
        setLoading(true);
        try {
            console.log('Sending HTTP query:', httpInput);
            // const response = await axios.post('http://localhost:4080/query', { 
            const response = await axios.post(process.env.REACT_APP_LLAMAINDEX_EXTENDED_NEURO_URL + '/query', {
                query: httpInput 
            });
            console.log('Received HTTP response:', response.data);
            const answer = response.data.answer || response.data.result || response.data;
            setHttpResult(typeof answer === 'string' ? answer : JSON.stringify(answer));
        } catch (error) {
            console.error('Error querying:', error);
            setHttpResult('Error: ' + error.response?.data?.error || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStreamQuery = () => {
        if (!streamInput.trim()) return;
        if (!socket?.connected) {
            console.error('Socket not connected');
            setStreamResult('Error: Socket not connected');
            return;
        }
        setLoading(true);
        setStreamResult('');
        console.log('Sending streaming query:', streamInput);
        
        // Set a timeout to reset loading state if stream_complete isn't received
        const timeoutId = setTimeout(() => {
            setLoading(false);
        }, 30000); // 30 second timeout
        
        socket.emit('stream_query', streamInput);
        
        // Store the timeout ID to clear it when stream_complete is received
        socket.timeoutId = timeoutId;
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">LlamaIndex Dashboard</h1>
            
            {/* Index Status and Load Button Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                    <div className="text-lg">
                        <span>Status: </span>
                        <span className="font-semibold">
                            {typeof indexStatus === 'object' ? indexStatus.status : indexStatus}
                        </span>
                        {typeof indexStatus === 'object' && indexStatus.timestamp && (
                            <span className="text-sm text-gray-500 ml-2">
                                (Updated: {new Date(indexStatus.timestamp).toLocaleTimeString()})
                            </span>
                        )}
                    </div>
                    <button 
                        onClick={handleLoadAndIndex}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-150 ease-in-out"
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Load and Index Documents'}
                    </button>
                </div>
            </div>

            {/* Query Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* HTTP Query Section */}
                <div className="p-4 border rounded-lg">
                    <h2 className="text-xl font-bold mb-4">HTTP Query</h2>
                    <div className="space-y-4">
                        <input
                            type="text"
                            value={httpInput}
                            onChange={(e) => setHttpInput(e.target.value)}
                            placeholder="Enter your HTTP query"
                            className="w-full border rounded p-2"
                        />
                        <button 
                            onClick={handleHttpQuery}
                            className="w-full bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition duration-150 ease-in-out"
                            disabled={loading || (typeof indexStatus === 'object' ? 
                                indexStatus.status !== 'Loaded' : 
                                indexStatus !== 'Loaded')}
                        >
                            Send HTTP Query
                        </button>
                        {httpResult && (
                            <div className="mt-4 p-4 bg-gray-50 rounded">
                                <h3 className="font-bold mb-2">Result:</h3>
                                <p className="whitespace-pre-wrap">{httpResult}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Streaming Query Section */}
                <div className="p-4 border rounded-lg">
                    <h2 className="text-xl font-bold mb-4">Streaming Query</h2>
                    <div className="space-y-4">
                        <input
                            type="text"
                            value={streamInput}
                            onChange={(e) => setStreamInput(e.target.value)}
                            placeholder="Enter your streaming query"
                            className="w-full border rounded p-2"
                        />
                        <button 
                            onClick={handleStreamQuery}
                            className="w-full bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition duration-150 ease-in-out"
                            disabled={loading || indexStatus !== 'Loaded'}
                        >
                            Start Stream
                        </button>
                        {streamResult && (
                            <div className="mt-4 p-4 bg-gray-50 rounded">
                                <h3 className="font-bold mb-2">Streaming Result:</h3>
                                <p className="whitespace-pre-wrap">{streamResult}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading Indicator */}
            {loading && (
                <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg">
                    Processing...
                </div>
            )}
        </div>
    );
};

export default LlamaIndexDashboard;
